import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import contentService from '../services/contentService'
import { supabase } from '../lib/supabase'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'
import { Badge } from '../components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { Progress } from '../components/ui/progress'
import { useToast } from '../hooks/use-toast'
import {
  User,
  Mail,
  Calendar,
  Shield,
  Key,
  Play,
  Clock,
  Bookmark,
  History,
  Film,
  MonitorPlay,
  ThumbsUp,
  CreditCard,
  ExternalLink,
  AlertCircle
} from 'lucide-react'

const Profile = () => {
  const { user, updateProfile, updatePassword, getUserProfile, isEmailConfirmed } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [isPasswordLoading, setIsPasswordLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [profileData, setProfileData] = useState({
    full_name: '',
    avatar_url: ''
  })
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  })
  const [stats, setStats] = useState(null)
  const [recentHistory, setRecentHistory] = useState([])
  const [continueWatching, setContinueWatching] = useState([])
  const [statsLoading, setStatsLoading] = useState(true)
  const [subscription, setSubscription] = useState(null)
  const [portalLoading, setPortalLoading] = useState(false)

  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab && ['overview', 'profile', 'security', 'account'].includes(tab)) {
      setActiveTab(tab)
    }
  }, [searchParams])

  useEffect(() => {
    if (user) {
      const profile = getUserProfile()
      setProfileData({
        full_name: profile?.full_name || '',
        avatar_url: profile?.avatar_url || ''
      })
      loadStats()
      loadSubscription()
    }
  }, [user, getUserProfile])

  const loadSubscription = async () => {
    if (!user) return
    const { data } = await supabase
      .from('profiles')
      .select('subscription_tier, subscription_status, subscription_current_period_end, slices')
      .eq('id', user.id)
      .single()
    if (data) setSubscription(data)
  }

  const handleManageSubscription = async () => {
    setPortalLoading(true)
    try {
      const { data, error } = await supabase.functions.invoke('create-billing-portal', {
        body: { return_url: `${window.location.origin}/profile` }
      })
      if (error) throw error
      if (data?.portal_url) window.location.href = data.portal_url
    } catch (err) {
      toast({ title: 'Error', description: err.message || 'Failed to open billing portal.', variant: 'destructive' })
    } finally {
      setPortalLoading(false)
    }
  }

  const loadStats = async () => {
    setStatsLoading(true)
    try {
      const [viewerStats, history, continuing] = await Promise.all([
        contentService.getViewerStats(user.id),
        contentService.getWatchHistory(user.id, 5),
        contentService.getContinueWatching(user.id, 5)
      ])
      setStats(viewerStats)
      setRecentHistory(history)
      setContinueWatching(continuing)
    } catch (error) {
      console.error('Error loading stats:', error)
    } finally {
      setStatsLoading(false)
    }
  }

  const handleProfileUpdate = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      await updateProfile(profileData)
      toast({ title: "Profile Updated", description: "Your profile has been updated successfully." })
    } catch (error) {
      toast({ title: "Update Failed", description: error.message || "Failed to update profile.", variant: "destructive" })
    } finally {
      setIsLoading(false)
    }
  }

  const handlePasswordUpdate = async (e) => {
    e.preventDefault()
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({ title: "Password Mismatch", description: "New passwords do not match.", variant: "destructive" })
      return
    }
    if (passwordData.newPassword.length < 8) {
      toast({ title: "Password Too Short", description: "Password must be at least 8 characters long.", variant: "destructive" })
      return
    }
    setIsPasswordLoading(true)
    try {
      await updatePassword(passwordData.newPassword)
      setPasswordData({ newPassword: '', confirmPassword: '' })
      toast({ title: "Password Updated", description: "Your password has been updated successfully." })
    } catch (error) {
      toast({ title: "Update Failed", description: error.message || "Failed to update password.", variant: "destructive" })
    } finally {
      setIsPasswordLoading(false)
    }
  }

  const formatDuration = (seconds) => {
    if (!seconds) return ''
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    if (h > 0) return `${h}h ${m}m`
    return `${m}m`
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <User className="w-16 h-16 mx-auto text-muted-foreground" />
              <div>
                <h2 className="text-xl font-semibold">Authentication Required</h2>
                <p className="text-muted-foreground">Please log in to view your profile.</p>
              </div>
              <Button onClick={() => navigate('/auth')} className="w-full">Sign In</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="space-y-8">
          {/* Header */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-accent/10 rounded-2xl" />
            <div className="relative p-8 rounded-2xl border bg-card/50 backdrop-blur-sm">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-6 lg:space-y-0">
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <Avatar className="h-24 w-24 ring-4 ring-background shadow-xl">
                      <AvatarImage src={profileData.avatar_url} alt={profileData.full_name} />
                      <AvatarFallback className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground text-2xl font-bold">
                        {profileData.full_name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="absolute -bottom-1 -right-1 bg-background rounded-full p-1">
                      {isEmailConfirmed() ? (
                        <Shield className="w-5 h-5 text-primary" />
                      ) : (
                        <Mail className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <h1 className="text-3xl font-bold">{profileData.full_name || 'Your Profile'}</h1>
                    <p className="text-muted-foreground text-lg">{user.email}</p>
                    <div className="flex items-center space-x-3">
                      
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-background/60 rounded-xl border">
                    <div className="text-2xl font-bold text-primary">{stats?.videosWatched ?? '-'}</div>
                    <div className="text-xs text-muted-foreground font-medium">Videos</div>
                  </div>
                  <div className="text-center p-3 bg-background/60 rounded-xl border">
                    <div className="text-2xl font-bold text-primary">{stats?.totalWatchTime ?? '-'}</div>
                    <div className="text-xs text-muted-foreground font-medium">Watch Time</div>
                  </div>
                  <div className="text-center p-3 bg-background/60 rounded-xl border">
                    <div className="text-2xl font-bold text-primary">{stats?.watchlistCount ?? '-'}</div>
                    <div className="text-xs text-muted-foreground font-medium">Watchlist</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 h-auto p-1 bg-muted/50">
              <TabsTrigger value="overview" className="flex items-center space-x-2 py-3">
                <Play className="w-4 h-4" />
                <span className="hidden sm:inline">Overview</span>
              </TabsTrigger>
              <TabsTrigger value="profile" className="flex items-center space-x-2 py-3">
                <User className="w-4 h-4" />
                <span className="hidden sm:inline">Profile</span>
              </TabsTrigger>
              <TabsTrigger value="security" className="flex items-center space-x-2 py-3">
                <Key className="w-4 h-4" />
                <span className="hidden sm:inline">Security</span>
              </TabsTrigger>
              <TabsTrigger value="account" className="flex items-center space-x-2 py-3">
                <Calendar className="w-4 h-4" />
                <span className="hidden sm:inline">Account</span>
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-8">
              <div className="grid gap-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-primary/80">Watch Time</p>
                          <p className="text-2xl font-bold text-primary">{stats?.totalWatchTime ?? '0m'}</p>
                        </div>
                        <Clock className="w-8 h-8 text-primary" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-primary/80">Completed</p>
                          <p className="text-2xl font-bold text-primary">{stats?.completedCount ?? 0}</p>
                        </div>
                        <Film className="w-8 h-8 text-primary" />
                      </div>
                      {stats && stats.videosWatched > 0 && (
                        <>
                          <Progress value={stats.completionRate} className="mt-3 h-2" />
                          <p className="text-xs text-primary/70 mt-2">{stats.completionRate}% completion rate</p>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-accent/5 to-accent/10 border-accent/20">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-accent/80">Watchlist</p>
                          <p className="text-2xl font-bold text-accent">{stats?.watchlistCount ?? 0}</p>
                        </div>
                        <Bookmark className="w-8 h-8 text-accent" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-muted/50 to-muted border-muted/50">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Ratings</p>
                          <p className="text-2xl font-bold text-foreground">{stats?.ratingsCount ?? 0}</p>
                        </div>
                        <ThumbsUp className="w-8 h-8 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Continue Watching */}
                {continueWatching.length > 0 && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="flex items-center space-x-2">
                        <Play className="w-5 h-5" />
                        <span>Continue Watching</span>
                      </CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/history')}>
                        View All
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {continueWatching.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center space-x-4 p-3 rounded-lg border bg-card/50 hover:bg-card transition-colors cursor-pointer"
                            onClick={() => navigate(`/watch/${item.slug}`)}
                          >
                            <div className="relative w-24 h-14 bg-muted rounded overflow-hidden flex-shrink-0">
                              {item.thumbnail ? (
                                <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover" />
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Play className="w-6 h-6 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium truncate">{item.title}</h4>
                              <div className="mt-1">
                                <Progress value={item.progress} className="h-1" />
                                <p className="text-xs text-muted-foreground mt-1">{item.progress}% watched</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recent History */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center space-x-2">
                      <History className="w-5 h-5" />
                      <span>Recent Activity</span>
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/history')}>
                      View All
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {recentHistory.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No watch history yet. Start watching to see your activity here.</p>
                        <Button variant="outline" className="mt-4" onClick={() => navigate('/browse')}>
                          Browse Content
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {recentHistory.map((item, i) => (
                          <div
                            key={`${item.id}-${i}`}
                            className="flex items-center space-x-4 p-3 rounded-lg border bg-card/50 hover:bg-card transition-colors cursor-pointer"
                            onClick={() => navigate(`/watch/${item.slug}`)}
                          >
                            <div className="relative w-24 h-14 bg-muted rounded overflow-hidden flex-shrink-0">
                              {item.thumbnail ? (
                                <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover" />
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Play className="w-6 h-6 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium truncate">{item.title}</h4>
                              {item.episodeTitle && (
                                <p className="text-sm text-muted-foreground truncate">
                                  S{item.seasonNumber} E{item.episodeNumber} - {item.episodeTitle}
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground">
                                {formatDuration(item.watchDuration)} watched
                                {item.completed && ' - Completed'}
                              </p>
                            </div>
                            <Badge variant={item.completed ? 'default' : 'secondary'} className="flex-shrink-0">
                              {item.completed ? 'Done' : `${item.progress}%`}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Links */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Button variant="outline" className="h-auto p-4 justify-start" onClick={() => navigate('/watchlist')}>
                    <Bookmark className="w-5 h-5 mr-3" />
                    <div className="text-left">
                      <div className="font-medium">My Watchlist</div>
                      <div className="text-sm text-muted-foreground">{stats?.watchlistCount ?? 0} saved</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-auto p-4 justify-start" onClick={() => navigate('/history')}>
                    <History className="w-5 h-5 mr-3" />
                    <div className="text-left">
                      <div className="font-medium">Watch History</div>
                      <div className="text-sm text-muted-foreground">{stats?.videosWatched ?? 0} watched</div>
                    </div>
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Profile Tab */}
            <TabsContent value="profile" className="mt-8">
              <Card className="max-w-xl">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <User className="w-5 h-5" />
                    <span>Profile Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleProfileUpdate} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="full_name">Full Name</Label>
                      <Input
                        id="full_name"
                        type="text"
                        value={profileData.full_name}
                        onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" type="email" value={user.email} disabled className="bg-muted" />
                      <p className="text-xs text-muted-foreground">Email cannot be changed. Contact support if needed.</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="avatar_url">Avatar URL</Label>
                      <Input
                        id="avatar_url"
                        type="url"
                        value={profileData.avatar_url}
                        onChange={(e) => setProfileData(prev => ({ ...prev, avatar_url: e.target.value }))}
                        placeholder="https://example.com/avatar.jpg"
                      />
                    </div>
                    <Button type="submit" disabled={isLoading} className="w-full">
                      {isLoading ? 'Updating...' : 'Update Profile'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security" className="mt-8">
              <Card className="max-w-xl">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Key className="w-5 h-5" />
                    <span>Change Password</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handlePasswordUpdate} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="newPassword">New Password</Label>
                      <Input
                        id="newPassword"
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                        placeholder="Enter new password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm New Password</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        placeholder="Confirm new password"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isPasswordLoading || !passwordData.newPassword || !passwordData.confirmPassword}
                      className="w-full"
                    >
                      {isPasswordLoading ? 'Updating...' : 'Update Password'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Account Tab */}
            <TabsContent value="account" className="mt-8 space-y-6">
              <Card className="max-w-xl">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Calendar className="w-5 h-5" />
                    <span>Account Details</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm font-medium">Member since</span>
                      <span className="text-sm text-muted-foreground">{new Date(user.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm font-medium">Email verified</span>
                      <span className="text-sm text-muted-foreground">{isEmailConfirmed() ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm font-medium">Account type</span>
                      <span className="text-sm text-muted-foreground">Viewer</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Subscription Management */}
              <Card className="max-w-xl">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <CreditCard className="w-5 h-5" />
                    <span>Subscription</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!subscription || ['None', 'New User'].includes(subscription.subscription_tier) ? (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">You don't have an active subscription.</p>
                      <Button onClick={() => navigate('/pricing')}>View Plans</Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <span className="text-sm font-medium">Plan</span>
                        <Badge variant="secondary">{subscription.subscription_tier}</Badge>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <span className="text-sm font-medium">Status</span>
                        {subscription.subscription_status === 'past_due' ? (
                          <Badge variant="destructive" className="flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Past Due
                          </Badge>
                        ) : (
                          <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                            {subscription.subscription_status === 'active' ? 'Active' : subscription.subscription_status}
                          </Badge>
                        )}
                      </div>
                      {subscription.subscription_current_period_end && (
                        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <span className="text-sm font-medium">Next billing date</span>
                          <span className="text-sm text-muted-foreground">
                            {new Date(subscription.subscription_current_period_end).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <span className="text-sm font-medium">Slices balance</span>
                        <span className="text-sm text-muted-foreground">{subscription.slices ?? 0}</span>
                      </div>
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          onClick={handleManageSubscription}
                          disabled={portalLoading}
                          className="flex items-center gap-2"
                        >
                          <ExternalLink className="w-4 h-4" />
                          {portalLoading ? 'Opening...' : 'Manage Subscription'}
                        </Button>
                        {subscription.subscription_status === 'past_due' && (
                          <Button onClick={() => navigate('/pricing')}>Update Payment</Button>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  )
}

export default Profile
